import React, { useRef, useCallback, useState } from 'react';
import { FiLock } from 'react-icons/fi';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { useHistory, useLocation } from 'react-router-dom';

import { useToast } from '../../context/ToastContext';
import { useIndexedDB } from '../../context/IndexedDBContext';
import { useConfigs } from '../../context/ConfigsContext';

import api from '../../services/api';

import getValidationErrors from '../../utils/getValidationErros';

import logoImg from '../../assets/Sizex Clin Logo.png';

import Input from '../../components/input';
import Button from '../../components/button';

import { Container, Content, AnimationContainer, Background } from './styles';

interface ResetPasswordFormData {
  password: string;
  password_confirmation: string;
}

const ResetPassword: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const formRef = useRef<FormHandles>(null);

  const { addToast } = useToast();
  const storage = useIndexedDB();
  const { cores } = useConfigs();

  const history = useHistory();
  const location = useLocation();

  const handleSubmit = useCallback(
    async (data: ResetPasswordFormData) => {
      try {
        setLoading(true);
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          password: Yup.string().required('Senha obrigatória').min(6, 'Mínimo 6 caracteres'),
          password_confirmation: Yup.string().oneOf(
            [Yup.ref('password')],
            'Confirmação incorreta!',
          ),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const { password, password_confirmation } = data;

        const params = new URLSearchParams(location.search);
        const token = params.get('token');

        if (!token) {
          throw new Error('Token não encontrado');
        }

        await api.post('/password/reset', {
          password,
          password_confirmation,
          token,
        });

        addToast({
          type: 'success',
          title: 'Senha alterada!',
          description: 'Sua senha foi redefinida com sucesso.',
        });

        const empresa = await storage.lerIndexedDB('empresa');
        history.push(`/${empresa}`);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro ao resetar senha',
          description:
            'Ocorreu um erro ao tentar resetar sua senha, tente novamente.',
        });
      } finally {
        setLoading(false);
      }
    },
    [history, addToast, location.search, storage],
  );

  return (
    <Container>
      <Content>
        <AnimationContainer cores={cores}>
          <img src={logoImg} alt="Logo Sizex" height={280} width={280} />

          <Form ref={formRef} onSubmit={handleSubmit}>
            <h1>Redefinir Senha</h1>

            <Input
              name="password"
              type="password"
              placeholder="Nova senha"
              icon={FiLock}
              autoFocus
            />
            <Input
              name="password_confirmation"
              type="password"
              placeholder="Confirmação da senha"
              icon={FiLock}
            />

            <Button loading={loading} type="submit">Alterar senha</Button>
          </Form>
        </AnimationContainer>
      </Content>

      <Background />
    </Container>
  );
};

export default ResetPassword;
